// src/App.jsx
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { AppProvider } from './context/AppContext';
import { StudentFormProvider } from './context/StudentFormContext';
import { QueryProvider } from './context/QueryContext'; // ✅ Import QueryProvider
import Layout from './components/Layout/Layout';
import RoleBasedLayout from './components/Layout/RoleBasedLayout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import EmployeeDashboard from './pages/EmployeeDashboard';
import Employees from './pages/Employees';
import AddEmployee from './pages/AddEmployee';
import EditEmployee from './pages/EditEmployee';
import Attendance from './pages/Attendance';
import Performance from './pages/Performance';
import Leave from './pages/Leave';
import LeavePage from './pages/Leave';
import ResetPassword from './pages/ResetPassword';
import DailyUpdates from './pages/DailyUpdates';
import Settings from './pages/Settings';
import { initializeData } from './services/dataService';
import { initializeAuth, getCurrentUser } from './services/authService';
import EmailApproval from './pages/EmailApproval';
import EmployeeStudentForm from './pages/EmployeeStudentForm';
import HRStudentFormDashboard from './pages/HRStudentFormDashboard';
import EmployeeQuery from './pages/EmployeeQuery'; // ✅ Import EmployeeQuery

// Reads role fresh on every render
const getHomeRoute = () => {
  const user = getCurrentUser();
  const isAdminOrHR = user?.role === 'admin' || user?.role === 'hr_manager';
  return isAdminOrHR ? '/dashboard' : '/employee-dashboard';
};

const RoleRedirect = () => {
  return <Navigate to={getHomeRoute()} replace />;
};

function AppContent() {
  const [, setAuthReady] = useState(false);

  useEffect(() => {
    initializeData();
    initializeAuth();
    setAuthReady(true);
  }, []);

  return (
    <Routes>
      {/* ── Public ──────────────────────────────────────────────────────── */}
      <Route path="/login" element={<Login />} />
      <Route path="/change-password" element={<ChangePassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/api/leave/email/approve/:token" element={<EmailApproval />} />
      <Route path="/api/leave/email/reject/:token" element={<EmailApproval />} />
      <Route path="/api/leave/email/view/:token" element={<EmailApproval />} />

      {/* ── Root → smart role redirect ───────────────────────────────────── */}
      <Route path="/" element={
        <ProtectedRoute>
          <RoleRedirect />
        </ProtectedRoute>
      } />

      {/* ── Admin / HR only ─────────────────────────────────────────────── */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/employees" element={
        <ProtectedRoute>
          <RoleBasedLayout allowedRoles={['admin', 'hr_manager']}>
            <Layout><Employees /></Layout>
          </RoleBasedLayout>
        </ProtectedRoute>
      } />

      <Route path="/employees/add" element={
        <ProtectedRoute>
          <RoleBasedLayout allowedRoles={['admin', 'hr_manager']}>
            <Layout><AddEmployee /></Layout>
          </RoleBasedLayout>
        </ProtectedRoute>
      } />

      <Route path="/employees/edit/:id" element={
        <ProtectedRoute>
          <RoleBasedLayout allowedRoles={['admin', 'hr_manager']}>
            <Layout><EditEmployee /></Layout>
          </RoleBasedLayout>
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute>
          <RoleBasedLayout allowedRoles={['admin', 'hr_manager']}>
            <Layout><Settings /></Layout>
          </RoleBasedLayout>
        </ProtectedRoute>
      } />

      <Route path="/daily-updates" element={
        <ProtectedRoute>
          <RoleBasedLayout allowedRoles={['admin', 'hr_manager']}>
            <Layout><DailyUpdates /></Layout>
          </RoleBasedLayout>
        </ProtectedRoute>
      } />

      {/* ✅ Student Forms - HR Dashboard */}
      <Route path="/hr-student-forms" element={
        <ProtectedRoute>
          <RoleBasedLayout allowedRoles={['admin', 'hr_manager']}>
            <Layout><HRStudentFormDashboard /></Layout>
          </RoleBasedLayout>
        </ProtectedRoute>
      } />

      {/* ✅ Employee Queries - Accessible by both Employee and HR */}
      <Route path="/queries" element={
        <ProtectedRoute>
          <Layout><EmployeeQuery /></Layout>
        </ProtectedRoute>
      } />

      {/* ── Employee only ────────────────────────────────────────────────── */}
      <Route path="/employee-dashboard" element={
        <ProtectedRoute>
          <RoleBasedLayout allowedRoles={['employee']}>
            <Layout><EmployeeDashboard /></Layout>
          </RoleBasedLayout>
        </ProtectedRoute>
      } />

      {/* ✅ Student Form - Employee only */}
      <Route path="/student-form" element={
        <ProtectedRoute>
          <RoleBasedLayout allowedRoles={['employee']}>
            <Layout><EmployeeStudentForm /></Layout>
          </RoleBasedLayout>
        </ProtectedRoute>
      } />

      {/* ── Shared (all roles) ───────────────────────────────────────────── */}
      <Route path="/attendance" element={
        <ProtectedRoute>
          <Layout><Attendance /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/performance" element={
        <ProtectedRoute>
          <Layout><Performance /></Layout>
        </ProtectedRoute>
      } />

      {/* Leave Routes */}
      <Route path="/leave/*" element={
        <ProtectedRoute>
          <Layout>
            <LeavePage />
          </Layout>
        </ProtectedRoute>
      } />

      {/* ── Catch-all ────────────────────────────────────────────────────── */}
      <Route path="*" element={
        <ProtectedRoute>
          <RoleRedirect />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

// ✅ CORRECT: AuthProvider wraps everything
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppProvider>
          <StudentFormProvider>
            <QueryProvider> {/* ✅ Add QueryProvider here */}
              <AppContent />
            </QueryProvider>
          </StudentFormProvider>
        </AppProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;